import type {
  SearchHook,
  SearchHookSuggestResult,
  SearchHookOptions,
  SearchHookSuggestion,
  SearchHookRetrieveResult,
  MapCoords,
  SearchHookSuggestOptions,
} from "../../../types";
import { useMemo } from "react";

import {
  config,
  geocoding,
  type GeocodingFeature,
  type GeocodingOptions,
} from "@maptiler/client";
import { suggestedFeatureToLocation } from "../utils";
import { isAbortError } from "../../../is-abort-error";

export function useMapSearch({
  token,
  proximity,
  types,
}: SearchHookOptions): SearchHook {
  config.apiKey = token;
  const searchOptions = useMemo(
    () => ({ proximity, types }),
    [proximity, types],
  );
  const mappedTypes: GeocodingOptions["types"] = useMemo(
    () =>
      searchOptions.types?.map((type) => {
        let mappedType: NonNullable<GeocodingOptions["types"]>[number];

        switch (type) {
          case "postcode":
            mappedType = "postal_code";
            break;
          case "district":
            mappedType = "municipal_district";
            break;
          case "neighborhood":
            mappedType = "neighbourhood";
            break;
          case "street":
            mappedType = "road";
            break;
          default:
            mappedType = type;
        }

        return mappedType;
      }) ?? [],
    [searchOptions.types],
  );

  async function suggest(
    text: string,
    { bbox }: SearchHookSuggestOptions = {},
  ): Promise<SearchHookSuggestResult<GeocodingFeature>> {
    try {
      const { features, attribution } = await geocoding.forward(text, {
        proximity: searchOptions.proximity
          ? [searchOptions.proximity.lng, searchOptions.proximity.lat]
          : undefined,
        types: mappedTypes,
        bbox: bbox
          ? [bbox.sw.lng, bbox.sw.lat, bbox.ne.lng, bbox.ne.lat]
          : undefined,
      });

      return {
        attribution,
        suggestions: features.map((feature) => ({
          name: feature.text,
          formattedLocation: feature.place_name,
          address: feature.address ?? "",
          language: feature.language,
          _raw: feature,
        })),
      };
    } catch (error) {
      if (isAbortError(error)) {
        return { attribution: "", suggestions: [] };
      }
      throw error;
    }
  }

  async function retrieve(
    suggestion: SearchHookSuggestion<GeocodingFeature>,
  ): Promise<SearchHookRetrieveResult> {
    try {
      const { features, attribution } = await geocoding.byId(
        suggestion._raw.id,
      );

      return {
        attribution,
        locations: features.map(suggestedFeatureToLocation),
      };
    } catch (error) {
      if (isAbortError(error)) {
        return { attribution: "", locations: [] };
      }
      throw error;
    }
  }

  async function fromCoords(coords: MapCoords) {
    try {
      const { features, attribution } = await geocoding.reverse([
        coords.lng,
        coords.lat,
      ]);

      return {
        attribution,
        locations: features.map(suggestedFeatureToLocation),
      };
    } catch (error) {
      if (isAbortError(error)) {
        return { attribution: "", locations: [] };
      }
      throw error;
    }
  }

  return {
    suggest,
    retrieve,
    fromCoords,
  };
}
